import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateProductsPlaceholderSubcategory,
  useUpdateProductsPlaceholderSubcategory,
} from '@/api/generated/products-placeholder-subcategories/products-placeholder-subcategories';
import {
  buildSubcategoryFormSchema,
  emptySubcategoryFormValues,
  type SubcategoryFormValues,
} from './subcategoryFormSchema';
import { toSubcategoryRequestBody } from './toSubcategoryRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  Type: 'type',
};

interface UseSubcategoryFormOptions {
  /** Bez `subcategoryId` = zakládání, s `subcategoryId` = editace. */
  subcategoryId?: string;
  initialValues?: SubcategoryFormValues;
  /** Voláno po úspěšném uložení (předává id uložené subkategorie) — invalidace, toast; NEzavírá okno. */
  onSaved: (id: string) => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulářová logika subkategorie placeholderu (sdílená pro zakládání i editaci) — TanStack Form
 * se Zod validací, create/update mutace a promítnutí serverových validačních chyb do polí.
 * `submit()` vrací, zda se uložení povedlo (pro „Uložit & zpět").
 */
export function useSubcategoryForm({
  subcategoryId,
  initialValues,
  onSaved,
  onError,
}: UseSubcategoryFormOptions) {
  const { t } = useTranslation('venua-planner');
  const schema = useMemo(() => buildSubcategoryFormSchema(t), [t]);

  const createMutation = useCreateProductsPlaceholderSubcategory();
  const updateMutation = useUpdateProductsPlaceholderSubcategory();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptySubcategoryFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toSubcategoryRequestBody(value);
      try {
        if (subcategoryId != null) {
          await updateMutation.mutateAsync({ id: subcategoryId, data });
          onSaved(subcategoryId);
        } else {
          const newId = await createMutation.mutateAsync({ data });
          onSaved(newId);
        }
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu. */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return {
    form,
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}

/** Typ instance TanStack Form pro detail subkategorie (pro propsy podřízených komponent). */
export type SubcategoryFormApi = ReturnType<typeof useSubcategoryForm>['form'];
